// extra-fx-film — camera/film emulation effects
// Light leaks, Film scratches + gate weave, Bloom (canvas), Lens flare, Chromatic aberration (radial)

const N = (v) => Number(v) || 0;

// ---- Light leaks ----
const lightLeaks = async (canvas, ctx, intensity, hue) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const inten = N(intensity) / 100;
    const hueVal = N(hue);
    const numLeaks = 1 + Math.floor(Math.random() * 3);
    for (let l = 0; l < numLeaks; l++) {
        const edge = Math.floor(Math.random() * 4);
        const leakH = Math.floor(h * (0.1 + Math.random() * 0.3));
        const startY = Math.floor(Math.random() * h);
        const [r, g, b] = hslToRgb(hueVal / 360, 0.8, 0.6);
        for (let y = 0; y < h; y++) {
            const distFromEdge = edge === 0 ? y : edge === 1 ? (h - y) : edge === 2 ? Math.abs(y - startY) : Math.abs(y - startY);
            const falloff = Math.max(0, 1 - distFromEdge / leakH);
            if (falloff <= 0) continue;
            const xRange = edge < 2 ? w : Math.floor(w * (0.3 + Math.random() * 0.4));
            const xStart = edge < 2 ? 0 : Math.floor(Math.random() * (w - xRange));
            for (let x = xStart; x < xStart + xRange; x++) {
                const i = (y * w + x) * 4;
                const xf = (x - xStart) / xRange;
                const leakStr = falloff * inten * Math.sin(xf * Math.PI);
                data[i] = Math.min(255, data[i] + r * leakStr);
                data[i+1] = Math.min(255, data[i+1] + g * leakStr);
                data[i+2] = Math.min(255, data[i+2] + b * leakStr);
            }
        }
    }
    ctx.putImageData(img, 0, 0);
};
function hslToRgb(h, s, l) {
    let r, g, b;
    if (s === 0) { r = g = b = l; }
    else {
        const hue2rgb = (p, q, t) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1/6) return p + (q - p) * 6 * t; if (t < 1/2) return q; if (t < 2/3) return p + (q - p) * (2/3 - t) * 6; return p; };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
        r = hue2rgb(p, q, h + 1/3); g = hue2rgb(p, q, h); b = hue2rgb(p, q, h - 1/3);
    }
    return [r * 255, g * 255, b * 255];
}

// ---- Film scratches + gate weave ----
const filmScratches = async (canvas, ctx, scratches, weave) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const numScr = Math.floor(N(scratches));
    const weaveAmt = N(weave);
    // Scratches
    for (let s = 0; s < numScr; s++) {
        const x = Math.floor(Math.random() * w);
        const len = Math.floor(h * (0.3 + Math.random() * 0.7));
        const y0 = Math.floor(Math.random() * h);
        const opacity = 0.3 + Math.random() * 0.5;
        for (let y = y0; y < y0 + len && y < h; y++) {
            const jitter = Math.floor((Math.random() - 0.5) * 2);
            const xi = Math.max(0, Math.min(w-1, x + jitter));
            const i = (y * w + xi) * 4;
            data[i] = data[i] * (1 - opacity) + 255 * opacity;
            data[i+1] = data[i+1] * (1 - opacity) + 255 * opacity;
            data[i+2] = data[i+2] * (1 - opacity) + 255 * opacity;
        }
    }
    // Gate weave
    if (weaveAmt > 0) {
        const out = new Uint8ClampedArray(data);
        for (let y = 0; y < h; y++) {
            const dx = Math.floor((Math.random() - 0.5) * weaveAmt);
            const dy = Math.floor((Math.random() - 0.5) * weaveAmt);
            for (let x = 0; x < w; x++) {
                const sx = Math.max(0, Math.min(w-1, x + dx));
                const sy = Math.max(0, Math.min(h-1, y + dy));
                const si = (sy * w + sx) * 4, di = (y * w + x) * 4;
                data[di] = out[si]; data[di+1] = out[si+1]; data[di+2] = out[si+2];
            }
        }
    }
    ctx.putImageData(img, 0, 0);
};

// ---- Bloom (canvas-based) ----
const bloomCanvas = async (canvas, ctx, threshold, intensity) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const thr = N(threshold), inten = N(intensity) / 100;
    // Create downscaled bloom buffer
    const bw = Math.max(1, Math.floor(w / 4)), bh = Math.max(1, Math.floor(h / 4));
    const bloom = new Float32Array(bw * bh * 4);
    for (let y = 0; y < bh; y++) for (let x = 0; x < bw; x++) {
        const sx = x * 4, sy = y * 4;
        let r = 0, g = 0, b = 0;
        for (let dy = 0; dy < 4; dy++) for (let dx = 0; dx < 4; dx++) {
            const i = ((sy+dy) * w + (sx+dx)) * 4;
            r += data[i]; g += data[i+1]; b += data[i+2];
        }
        r /= 16; g /= 16; b /= 16;
        const lum = 0.299*r + 0.587*g + 0.114*b;
        if (lum > thr) { bloom[(y*bw+x)*4] = r; bloom[(y*bw+x)*4+1] = g; bloom[(y*bw+x)*4+2] = b; }
    }
    // Simple box blur (3 passes)
    for (let pass = 0; pass < 3; pass++) {
        const tmp = new Float32Array(bloom);
        for (let y = 1; y < bh-1; y++) for (let x = 1; x < bw-1; x++) {
            for (let c = 0; c < 3; c++) {
                const i = (y*bw+x)*4+c;
                bloom[i] = (tmp[i] + tmp[i-4] + tmp[i+4] + tmp[i-bw*4] + tmp[i+bw*4]) / 5;
            }
        }
    }
    // Screen blend back
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const bx = Math.floor(x / 4), by = Math.floor(y / 4);
        const bi = (by * bw + bx) * 4, di = (y * w + x) * 4;
        for (let c = 0; c < 3; c++) {
            const bVal = bloom[bi + c] * inten;
            data[di + c] = 255 - ((255 - data[di + c]) * (255 - bVal) / 255);
        }
    }
    ctx.putImageData(img, 0, 0);
};

// ---- Lens flare ----
const lensFlare = async (canvas, ctx, x, y, intensity) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const cx = N(x) / 100 * w, cy = N(y) / 100 * h;
    const inten = N(intensity) / 100;
    // Core glow
    for (let py = 0; py < h; py++) for (let px = 0; px < w; px++) {
        const dx = px - cx, dy = py - cy;
        const dist = Math.sqrt(dx*dx + dy*dy);
        const i = (py * w + px) * 4;
        // Core
        const core = Math.max(0, 1 - dist / (w * 0.05)) * inten * 200;
        // Halo
        const halo = Math.max(0, 1 - dist / (w * 0.15)) * inten * 80;
        // Anamorphic streak (horizontal)
        const streak = Math.max(0, 1 - Math.abs(dy) / 3) * Math.max(0, 1 - Math.abs(dx) / (w * 0.3)) * inten * 150;
        data[i] = Math.min(255, data[i] + core + halo + streak * 0.3);
        data[i+1] = Math.min(255, data[i+1] + core * 0.7 + halo * 0.5 + streak * 0.5);
        data[i+2] = Math.min(255, data[i+2] + core * 0.3 + halo * 0.8 + streak);
    }
    // Ghost circles along line from center to light source
    const centerX = w / 2, centerY = h / 2;
    for (let g = 0; g < 5; g++) {
        const t = (g + 1) / 6;
        const gx = centerX + (cx - centerX) * t * 2;
        const gy = centerY + (cy - centerY) * t * 2;
        const gr = w * 0.03 * (1 + g * 0.3);
        const [r, gg, b] = [[1,0.3,0.3],[0.3,1,0.5],[0.3,0.5,1],[1,1,0.3],[0.5,0.3,1]][g];
        for (let py = Math.max(0, Math.floor(gy - gr)); py < Math.min(h, gy + gr); py++) {
            for (let px = Math.max(0, Math.floor(gx - gr)); px < Math.min(w, gx + gr); px++) {
                const d = Math.sqrt((px-gx)**2 + (py-gy)**2);
                if (d > gr) continue;
                const a = Math.max(0, 1 - d / gr) * inten * 60;
                const i = (py * w + px) * 4;
                data[i] = Math.min(255, data[i] + r * a);
                data[i+1] = Math.min(255, data[i+1] + gg * a);
                data[i+2] = Math.min(255, data[i+2] + b * a);
            }
        }
    }
    ctx.putImageData(img, 0, 0);
};

// ---- Chromatic aberration (radial) ----
const chromaticRadial = async (canvas, ctx, amount) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const src = new Uint8ClampedArray(img.data);
    const amt = N(amount);
    const cx = w / 2, cy = h / 2;
    const maxDist = Math.sqrt(cx*cx + cy*cy);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const dx = x - cx, dy = y - cy;
        const dist = Math.sqrt(dx*dx + dy*dy);
        const factor = (dist / maxDist) * amt;
        const ux = dx / (dist || 1), uy = dy / (dist || 1);
        const di = (y * w + x) * 4;
        // R channel shifts outward
        const rx = Math.round(x + ux * factor), ry = Math.round(y + uy * factor);
        const ri = (Math.max(0, Math.min(h-1, ry)) * w + Math.max(0, Math.min(w-1, rx))) * 4;
        img.data[di] = src[ri];
        // B channel shifts inward
        const bx = Math.round(x - ux * factor), by = Math.round(y - uy * factor);
        const bi = (Math.max(0, Math.min(h-1, by)) * w + Math.max(0, Math.min(w-1, bx))) * 4;
        img.data[di+2] = src[bi+2];
    }
    ctx.putImageData(img, 0, 0);
};

const descriptors = [
    { name: 'film-light-leaks', inputs: [
        { name: 'Intensity', type: 'knob', min: 0, max: 100, step: 1, value: 40 },
        { name: 'Hue', type: 'knob', min: 0, max: 360, step: 1, value: 30 }], fn: lightLeaks },
    { name: 'film-scratches', inputs: [
        { name: 'Scratches', type: 'knob', min: 0, max: 30, step: 1, value: 5 },
        { name: 'Weave', type: 'knob', min: 0, max: 10, step: 1, value: 2 }], fn: filmScratches },
    { name: 'film-bloom', inputs: [
        { name: 'Threshold', type: 'knob', min: 0, max: 255, step: 1, value: 180 },
        { name: 'Intensity', type: 'knob', min: 0, max: 100, step: 1, value: 50 }], fn: bloomCanvas },
    { name: 'film-lens-flare', inputs: [
        { name: 'X', type: 'knob', min: 0, max: 100, step: 1, value: 30 },
        { name: 'Y', type: 'knob', min: 0, max: 100, step: 1, value: 30 },
        { name: 'Intensity', type: 'knob', min: 0, max: 100, step: 1, value: 50 }], fn: lensFlare },
    { name: 'film-chromatic-radial', inputs: [
        { name: 'Amount', type: 'knob', min: 0, max: 30, step: 1, value: 8 }], fn: chromaticRadial },
];

const lib = {};
descriptors.forEach(d => lib[d.name] = d.fn);

module.exports = { descriptors, lib };
